import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Row,
  Column,
  Heading,
  Spacer,
  Text,
  ButtonGroup,
  Flex,
  Badge,
  withErrorBoundary,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { round } from '@oliasoft-open-source/units';
import { selectCostTrackerList, selectCostTrackerByCostList } from 'src/store/entities/cost-tracker/selectors';
import {
  CostTrackerItem,
  GroupedCostTrackerItem,
  CostTrackerByCostItem,
} from 'src/common/interfaces/cost-tracker.interfaces';
import { IProjectsOverview } from 'src/common/interfaces/project';
import type { TRootState } from 'src/store/store-types';
import { thousandsSeparators } from './cost-tracker-common/utils/utils';

type CostTrackerHeaderProps = {
  well: { name: string; wellId: string };
  estimatedCost: number;
  onViewModeChange: (key: number) => void;
  viewMode: number;
  actualByCost: number;
  totalVariance: number;
  isPositiveVariance: boolean;
  primaryCurrency: string;
};

const CostTrackerHeader = ({
  well,
  estimatedCost,
  onViewModeChange,
  viewMode,
  actualByCost,
  totalVariance,
  isPositiveVariance,
  primaryCurrency,
}: CostTrackerHeaderProps) => {
  const { t } = useTranslation();
  return (
    <>
      <Flex gap justifyContent="space-between">
        <Flex gap="20px">
          <Badge title="BETA" small>
            <Heading top testId="cost-tracker-title">
              {t(translations.costTracker_costTracker)}
            </Heading>
          </Badge>
          <ButtonGroup
            items={[
              { key: 1, icon: 'table' },
              { key: 0, icon: 'chart' },
            ]}
            small
            onSelected={(key: number) => onViewModeChange(key)}
            value={viewMode ? 1 : 0}
            disabled
          />
        </Flex>
      </Flex>
      <Spacer />
      <Row>
        <Column flex={false}>
          <Row spacing={0}>
            <Text bold>{t(translations.timeTracker_wellName)}</Text>
          </Row>
          <Row spacing={0}>
            <Text>{well?.name}</Text>
          </Row>
        </Column>
        <Column flex={false}>
          <Row spacing={0}>
            <Text bold>{t(translations.costTracker_EstimatedCost)}</Text>
          </Row>
          <Row spacing={0}>
            <Text>{`${thousandsSeparators(estimatedCost)} ${primaryCurrency}`}</Text>
          </Row>
        </Column>
        <Column flex={false}>
          <Row spacing={0}>
            <Text bold>{'Actual cost'}</Text>
          </Row>
          <Row spacing={0}>
            <Text>{`${thousandsSeparators(actualByCost)} ${primaryCurrency}`}</Text>
          </Row>
        </Column>
        <Column flex={false}>
          <Row spacing={0}>
            <Text bold>{'Total variance'}</Text>
          </Row>
          <Row spacing={0}>
            <Text
              error={isPositiveVariance}
              success={!isPositiveVariance}
            >{`${isPositiveVariance ? '+' : '-'} ${thousandsSeparators(totalVariance)} ${primaryCurrency}`}</Text>
          </Row>
        </Column>
      </Row>
    </>
  );
};

const mapStateToProps = ({ entities }: TRootState) => {
  const { well } = entities.projects.overview as IProjectsOverview;
  const list = selectCostTrackerList(entities) ?? [];
  const listByCost = selectCostTrackerByCostList(entities) ?? [];
  const flatList: CostTrackerItem[] = list?.map((section: GroupedCostTrackerItem) => section.operations).flat();
  const flatListByCost: CostTrackerByCostItem[] = Object.values(listByCost).flat();
  const estimatedCost = round(
    flatList.reduce((acc: number, operation) => acc + (operation.costAfe ?? 0), 0),
    0,
  );
  const actualByCost = round(
    flatListByCost.reduce((acc: number, cost) => acc + (cost.costActual ?? 0), 0),
    0,
  );
  const totalVariance = Math.abs(actualByCost - estimatedCost);
  const isPositiveVariance = actualByCost > estimatedCost;
  return {
    well,
    estimatedCost,
    actualByCost,
    totalVariance,
    isPositiveVariance,
  };
};

const Container = withErrorBoundary(connect(mapStateToProps)(CostTrackerHeader));

export { Container as CostTrackerHeader };
